import { CANNON_LADDER, CANNON_TUNING } from "../../features/cannon/cannonTuning";

/**
 * Cannon fixture helpers — keep every fixture's cannon fires on the same growing ladder as the
 * client's spin-start default (`CANNON_LADDER`). The queue itself is reset to the ladder at spin
 * boundary start (client-side), so fixtures only author the FIRE per winning substep.
 *
 * `apply_win_multiplier` is GAME-DEFINED (it left the platform's canonical set — the game registers
 * its handler via the cannon feature's plugin), so the action is authored HERE as a plain
 * `{ type, phase, payload }` literal, not via the shell `fx` DSL. Phase "win" is REQUIRED — the
 * platform's type→phase resolver no longer knows the type (unknown → "other"), and the fire must
 * always present. The backend carries the same contract (`actionPhaseName: "win"`).
 *
 * `substepIndex` is 0-based across the spin (0 = first winning substep, fires x2, …).
 */

/** The queued rounds the live cannon shows (one per placeholder bone behind the chamber), so a fixture never authors more. */
const QUEUE_WINDOW = CANNON_TUNING.spine.placeholders.length - 1;

/** The multiplier the cannon fires on the Nth winning substep — for the win's base × mult label. */
function cannonMult(substepIndex: number): string {
  return CANNON_LADDER[Math.min(substepIndex, CANNON_LADDER.length - 1)];
}

/**
 * The `apply_win_multiplier` LOADOUT (the chamber and the queue, nothing fired) at the Nth winning substep, as the
 * backend sends it at each reveal (its `cannonLoadoutAt`). A base spin's is substep 0; a free spin's is where the
 * session's cannon is up to, since the cannon climbs across a whole free-spins session.
 */
export function cannonLoadout(substepIndex: number) {
  const last = CANNON_LADDER.length - 1;
  const index = Math.min(substepIndex, last);
  return {
    type: "apply_win_multiplier",
    phase: "win" as const, // REQUIRED — see the header
    payload: {
      chamberedMultiplier: CANNON_LADDER[index],
      upcomingMultipliers: CANNON_LADDER.slice(index + 1, index + 1 + QUEUE_WINDOW),
    },
  };
}

/** The `apply_win_multiplier` FIRE action for the Nth winning substep (fires + advances the queue). */
export function cannonFire(substepIndex: number) {
  const last = CANNON_LADDER.length - 1;
  const chambered = CANNON_LADDER[Math.min(substepIndex + 1, last)];
  const upcoming = CANNON_LADDER.slice(substepIndex + 2, substepIndex + 2 + QUEUE_WINDOW);
  return {
    type: "apply_win_multiplier",
    phase: "win" as const, // REQUIRED — game-defined type, the resolver no longer maps it (see header)
    payload: {
      firedMultiplier: cannonMult(substepIndex),
      chamberedMultiplier: chambered,
      upcomingMultipliers: [...upcoming],
    },
  };
}
